import { useEffect, useRef, useState } from "react";
import { Search, X, Pill, ExternalLink } from "lucide-react";
import { api } from "../lib/api";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

/**
 * Searchable medication picker backed by CIMA-AEMPS (via /api/vademecum/search).
 * Falls back to a text input when the user types free-form.
 *
 *  onSelect(item) receives: { nombre, nregistro, principiosActivos: [], receta }
 *  If you just need a string, use onSelect((it)=>{ setValue(it.nombre) }).
 */
export default function VademecumPicker({ value, onChange, onSelect, placeholder = "Buscar en Vademécum CIMA...", testid = "vademecum-picker" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState(value || "");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const tRef = useRef(null);

  useEffect(() => { setQ(value || ""); }, [value]);

  useEffect(() => {
    if (!open) return;
    if (tRef.current) clearTimeout(tRef.current);
    if (!q || q.trim().length < 2) { setItems([]); return; }
    tRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/vademecum/search", { params: { q } });
        setItems(data.resultados || []);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    }, 320);
    return () => tRef.current && clearTimeout(tRef.current);
  }, [q, open]);

  const pick = (it) => {
    if (onSelect) onSelect(it);
    if (onChange) onChange(it.nombre);
    setQ(it.nombre);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => { setQ(e.target.value); onChange && onChange(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder={placeholder}
            className="pl-8 pr-8"
            data-testid={testid}
          />
          {q && (
            <button type="button" onClick={() => { setQ(""); onChange && onChange(""); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label="Limpiar">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0 max-h-80 overflow-y-auto" align="start" onOpenAutoFocus={(e) => e.preventDefault()}>
        {loading && <div className="p-3 text-xs text-muted-foreground">Buscando en CIMA-AEMPS...</div>}
        {!loading && items.length === 0 && q.length >= 2 && (
          <div className="p-3 text-xs text-muted-foreground">Sin resultados en CIMA para «{q}».</div>
        )}
        {!loading && items.length === 0 && q.length < 2 && (
          <div className="p-3 text-xs text-muted-foreground">Escriba al menos 2 caracteres...</div>
        )}
        {items.map((it) => (
          <button
            key={it.nregistro}
            type="button"
            onClick={() => pick(it)}
            className="w-full text-left px-3 py-2 border-b border-border last:border-b-0 hover:bg-muted flex items-start gap-2"
            data-testid={`vademecum-item-${it.nregistro}`}
          >
            <Pill className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">{it.nombre}</div>
              <div className="text-[11px] text-muted-foreground flex items-center gap-2 flex-wrap">
                {it.labtitular && <span>{it.labtitular}</span>}
                {it.principiosActivos?.length > 0 && <span>· {it.principiosActivos.slice(0, 2).join(", ")}</span>}
                {it.receta && <span className="text-red-700">· Con receta</span>}
                <span className="ml-auto text-primary inline-flex items-center gap-0.5">CIMA <ExternalLink className="w-3 h-3" /></span>
              </div>
            </div>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

/** Compact interactions checker: given a list of principios activos, show warnings. */
export function InteractionsBanner({ actives }) {
  const [warns, setWarns] = useState([]);
  useEffect(() => {
    const list = (actives || []).filter(Boolean);
    if (list.length < 2) { setWarns([]); return; }
    api.post("/vademecum/interactions", { actives: list })
      .then(({ data }) => setWarns(data.interactions || []))
      .catch(() => setWarns([]));
  }, [JSON.stringify(actives)]); // eslint-disable-line

  if (!warns.length) return null;
  return (
    <div className="rounded-lg border border-red-300 bg-red-50 p-3 space-y-1" data-testid="interactions-banner">
      <div className="text-xs font-semibold text-red-800">⚠ Interacciones detectadas ({warns.length})</div>
      {warns.map((w, i) => (
        <div key={i} className="text-xs text-red-900">
          <span className="font-medium">{w.a} + {w.b}</span> · <span className="uppercase text-[10px] font-semibold">{w.severity}</span> — {w.note}
        </div>
      ))}
    </div>
  );
}

export { Button };
